import { BaseTag } from '@praetorian-chariot/ui';

import { AttackSurfaceSelector } from '@/components/AttackSurfaceSelector';
import { SUBSCRIPTION_TIERS } from '@/sections/settings/subscription/constant';
import { FeatureCategory } from '@/sections/settings/subscription/tier';
import { formatPrice } from '@/sections/settings/subscription/util';
import { MonitoringCounts, SubscriptionConfiguration } from '@/types';
import { cn } from '@/utils/classname';

export function SubscriptionSummary(
  props: SubscriptionConfiguration & {
    pricingCurrency: string;
    billingCycle: 'annual' | 'monthly';
    billingPaymentMethod: string;
    monitoringCounts?: MonitoringCounts;
  }
) {
  // Calculate term in months from start and end dates
  const calculateTermInMonths = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return 12; // Default to 12 months

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Calculate year difference in months
    const yearsDiff = end.getFullYear() - start.getFullYear();
    const monthsDiff = end.getMonth() - start.getMonth();

    // Total months between dates
    let totalMonths = yearsDiff * 12 + monthsDiff;

    // Add 1 if end day is greater than or equal to start day (full month)
    if (end.getDate() >= start.getDate()) {
      totalMonths += 1;
    }

    return Math.max(1, totalMonths); // Ensure at least 1 month
  };

  const termInMonths = calculateTermInMonths(props.startDate, props.endDate);
  const features = SUBSCRIPTION_TIERS.find(
    tier => tier.id === props.selectedTier
  )?.features;

  // Calculate current totals from monitoring data
  const calculateCurrentAssets = () => {
    if (!props.monitoringCounts) return 0;
    const { applications, ...restMonitoringCounts } = props.monitoringCounts;

    return Object.keys(restMonitoringCounts).reduce((acc, curr) => {
      const category = props.monitoringCounts![curr as keyof MonitoringCounts];
      if (typeof category === 'object' && category !== null) {
        const categoryTotal = Object.values(category).reduce<number>(
          (sum, value) => {
            if (typeof value === 'object' && value !== null) {
              return (
                sum +
                Object.values(value).reduce<number>(
                  (s, v) => s + (v as number),
                  0
                )
              );
            }
            return sum + (value as number);
          },
          0
        );
        return acc + categoryTotal;
      }
      return acc;
    }, 0);
  };

  const calculateCurrentApplications = () => {
    if (!props.monitoringCounts?.applications) return 0;
    return props.monitoringCounts.applications.webseed || 0;
  };

  const currentAssets = calculateCurrentAssets();
  const currentApplications = calculateCurrentApplications();

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Subscription Summary Card */}
      <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border p-6">
        <h3 className="text-chariot-text-primary mb-6 text-lg font-semibold">
          Subscription Summary
        </h3>

        {/* Price Display */}
        <div className="mb-6">
          <div className="flex items-baseline gap-2">
            <span className="text-chariot-brand-primary text-4xl font-bold">
              {formatPrice(props.estimatedCost, props.pricingCurrency)}
            </span>
            <span className="text-chariot-text-primary text-lg">/ year</span>
          </div>
        </div>

        {/* Billing Details */}
        <div className="mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-chariot-text-primary text-base">
              Start Date
            </span>
            <span className="text-chariot-text-primary text-base font-semibold">
              {props.startDate || 'Not set'}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-chariot-text-primary text-base">
              End Date
            </span>
            <span className="text-chariot-text-primary text-base font-semibold">
              {props.endDate || 'Not set'}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-chariot-text-primary text-base">Term</span>
            <span className="text-chariot-text-primary text-base font-semibold">
              {termInMonths} {termInMonths === 1 ? 'month' : 'months'}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-chariot-text-primary text-base">
              Payment Method
            </span>
            <span className="text-chariot-text-primary text-base font-semibold">
              {props.billingPaymentMethod}
            </span>
          </div>
        </div>

        <div className="border-chariot-stroke border-t pt-4">
          {/* Contract Details */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-chariot-text-primary text-base">
                Asset Current Coverage vs Asset Contract Limit
              </span>
              <div className="flex items-center gap-1">
                <span
                  className={cn(
                    'text-base font-semibold',
                    currentAssets > props.numberOfAssets
                      ? 'text-red-500'
                      : 'text-chariot-brand-primary'
                  )}
                >
                  {currentAssets.toLocaleString()}
                </span>
                <span className="text-chariot-text-primary text-base">/</span>
                <span className="text-chariot-brand-primary text-base font-semibold">
                  {props.numberOfAssets.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-chariot-text-primary text-base">
                Application Current Coverage vs Application Contract Limit
              </span>
              <div className="flex items-center gap-1">
                <span
                  className={cn(
                    'text-base font-semibold',
                    currentApplications > props.numberOfApplications
                      ? 'text-red-500'
                      : 'text-chariot-brand-primary'
                  )}
                >
                  {currentApplications.toLocaleString()}
                </span>
                <span className="text-chariot-text-primary text-base">/</span>
                <span className="text-chariot-brand-primary text-base font-semibold">
                  {props.numberOfApplications}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-chariot-text-primary text-base">
                Scanning Frequency
              </span>
              <span className="text-chariot-brand-primary text-base font-semibold capitalize">
                {props.scanSchedule}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-chariot-text-primary text-base">
                Attack Surface Coverage
              </span>
              <div className="flex justify-end">
                <AttackSurfaceSelector
                  value={{
                    external: props.attackSurface.external || false,
                    applications: props.attackSurface.applications || false,
                    internal: props.attackSurface.internal || false,
                    cloud: props.attackSurface.cloud || false,
                    code: props.attackSurface.code || false,
                    users: props.attackSurface.users || false,
                    device: props.attackSurface.device || false,
                  }}
                  onChange={() => {}} // Read-only in summary view
                  variant="compact"
                  showLabels={true}
                  disabledOptions={[]} // No disabled options, just show selected state
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Tier Card */}
      {features && (
        <div className="border-chariot-stroke bg-chariot-background-primary flex flex-col gap-4 rounded-lg border p-6">
          <h3 className="text-chariot-text-primary text-lg font-semibold">
            Subscription Tier
          </h3>
          <BaseTag
            className="border-chariot-brand-primary bg-chariot-background-secondary/40 capitalize"
            size="sm"
          >
            {props.selectedTier}
          </BaseTag>
          <div className="space-y-2">
            {Object.entries(features).map(([categoryName, features]) => (
              <FeatureCategory
                key={categoryName}
                categoryName={categoryName}
                features={features}
                removeTextHighlight={true}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
